import { type DocNode } from '@atlaskit/adf-schema';
import { reduce } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { ROVO_SKILL_COMBINED_EXTENSION_KEY } from '@atlassian/conversation-assistant-content-transformer/utils/skills';

export type ContentFeatures = {
	adfLength: number;
	textLength: number;
	wordCount: number;
	linkCount: number;
	mentionCount: number;
	explicitSkills: string[];
};

const createSegmenter = (): Intl.Segmenter | null => {
	try {
		return Intl?.Segmenter ? new Intl.Segmenter(undefined, { granularity: 'word' }) : null;
	} catch {
		return null;
	}
};
const segmenter = createSegmenter();

const wordCount = (text?: string): number => {
	if (!text) return 0;
	// We use Intl here since it is locale-aware and accurately handles word counts across all languages. The regex will undercount non-English languages.
	if (segmenter) {
		return [...segmenter.segment(text)].filter((s) => s.isWordLike).length;
	}
	return text.match(/\S+/g)?.length ?? 0;
};
const processADFNode = (currentValue: ContentFeatures, node: ADFEntity): ContentFeatures => {
	switch (node.type) {
		case 'text':
			const linkCount = node.marks?.some((mark) => mark.type === 'link')
				? currentValue.linkCount + 1
				: currentValue.linkCount;
			return {
				...currentValue,
				textLength: currentValue.textLength + (node.text ? node.text.length : 0),
				wordCount: currentValue.wordCount + wordCount(node.text),
				linkCount,
			};
		case 'mention':
			return {
				...currentValue,
				mentionCount: currentValue.mentionCount + 1,
			};
		case 'inlineCard':
		case 'embedCard':
			return {
				...currentValue,
				linkCount: currentValue.linkCount + 1,
			};
		case 'inlineExtension':
			if (
				node.attrs?.extensionKey === ROVO_SKILL_COMBINED_EXTENSION_KEY &&
				node.attrs?.parameters?.slug
			) {
				return {
					...currentValue,
					explicitSkills: [...currentValue.explicitSkills, node.attrs.parameters.slug],
				};
			}
			return currentValue;
	}
	return currentValue;
};

export const extractContentFeatures = (adf: DocNode): ContentFeatures => {
	const initialFeatures: ContentFeatures = {
		adfLength: JSON.stringify(adf).length,
		textLength: 0,
		wordCount: 0,
		linkCount: 0,
		mentionCount: 0,
		explicitSkills: [],
	};

	return reduce<ContentFeatures>(
		adf,
		(currentValue, node: ADFEntity) => processADFNode(currentValue, node),
		initialFeatures,
	);
};

/**
 * Extracts plain text from an ADF document (used e.g. for comparing sent message to prompt card source).
 */
export const extractPlainTextFromAdf = (adf: DocNode): string =>
	reduce<string>(
		adf,
		(currentValue, node: ADFEntity) =>
			node.type === 'text' && node.text ? currentValue + node.text : currentValue,
		'',
	);

/**
 * Backtick-wrapped placeholders in prompt cards (e.g. `document`, `person`) are meant to be replaced by the user.
 * When comparing sent text to the card prompt, we treat these as variable and only require fixed segments to match.
 */
const BACKTICK_PLACEHOLDER_REGEX = /`[^`]*`/g;

export type PromptCardSendType = 'asIs' | 'placeholdersFilled' | 'tweaked';

/**
 * Single source of truth: classifies how the user sent a prompt that came from a card. Returns null if the sent
 * text is not related to the card (e.g. user cleared and typed something else).
 * - Exact match → 'asIs'
 * - Card has placeholders, fixed segments match in order → 'placeholdersFilled'
 * - Related but edited ie user added or removed text → 'tweaked'
 * - User clicks prompt, deletes it, and types a prompt → null
 */
export const getPromptCardSendType = (
	normalizedSentText: string,
	normalizedOriginalPrompt: string,
): PromptCardSendType | null => {
	if (normalizedSentText === normalizedOriginalPrompt) {
		return 'asIs';
	}
	if (normalizedOriginalPrompt.length === 0) {
		return null;
	}
	const fixedSegments = normalizedOriginalPrompt.split(BACKTICK_PLACEHOLDER_REGEX);
	const hasPlaceholders = fixedSegments.length > 1;
	if (hasPlaceholders) {
		let searchFrom = 0;
		let firstSegmentMatchedAtStart = true;
		for (const segment of fixedSegments) {
			const trimmed = segment.trim();
			if (trimmed.length === 0) {
				continue;
			}
			const idx = normalizedSentText.indexOf(trimmed, searchFrom);
			if (idx === -1) {
				// User truncated or edited after matching some of the card — if we matched at least one
				// fixed segment, they started from the card so treat as tweaked
				if (searchFrom > 0) {
					return 'tweaked';
				}
				// No segment matched yet; check containment (strip placeholders so "on `x`" vs "on x" match)
				const originalWithoutPlaceholders = normalizedOriginalPrompt
					.replace(BACKTICK_PLACEHOLDER_REGEX, ' ')
					.replace(/\s+/g, ' ')
					.trim();
				if (
					normalizedOriginalPrompt.includes(normalizedSentText) ||
					normalizedSentText.includes(normalizedOriginalPrompt) ||
					originalWithoutPlaceholders.includes(normalizedSentText) ||
					normalizedSentText.includes(originalWithoutPlaceholders) ||
					originalWithoutPlaceholders.startsWith(normalizedSentText) ||
					normalizedSentText.startsWith(originalWithoutPlaceholders)
				) {
					return 'tweaked';
				}
				return null;
			}
			if (searchFrom === 0 && idx !== 0) {
				firstSegmentMatchedAtStart = false;
			}
			searchFrom = idx + trimmed.length;
		}
		// Trailing content after the last fixed segment
		if (normalizedSentText.length > searchFrom) {
			return 'tweaked';
		}
		return firstSegmentMatchedAtStart ? 'placeholdersFilled' : 'tweaked';
	}
	if (
		normalizedSentText.includes(normalizedOriginalPrompt) ||
		normalizedOriginalPrompt.includes(normalizedSentText)
	) {
		return 'tweaked';
	}
	return null;
};

/**
 * Checks whether a top-level adf block has a given localId anywhere in its subtree.
 */
const containsLocalId = (node: ADFEntity, localId: string): boolean => {
	if (node.attrs?.localId === localId) return true;
	return ((node.content as ADFEntity[]) ?? []).some((child) => containsLocalId(child, localId));
};

/**
 * Returns the unique top-level block types that contain a selection.
 * Uses fullAdf + localIds because the editor gives you the contents of a selection, not its container.
 * example: selecting a table gives you its inner paragraphs, not the table itself.
 */
export const getTopLevelNodeTypes = (
	fullAdf: DocNode,
	startLocalId: string,
	endLocalId: string,
): string[] => {
	if (!startLocalId || !endLocalId) return [];

	try {
		const topLevelNodes = (fullAdf?.content ?? []) as ADFEntity[];

		// A selection anchor can be anywhere in the doc tree, not just at the top level. We need to know which top-level ADF component the selection belongs to.
		let startNodeIndex: number | undefined;
		let endNodeIndex: number | undefined;

		for (let i = 0; i < topLevelNodes.length; i++) {
			if (startNodeIndex === undefined && containsLocalId(topLevelNodes[i], startLocalId))
				startNodeIndex = i;
			if (endNodeIndex === undefined && containsLocalId(topLevelNodes[i], endLocalId))
				endNodeIndex = i;

			if (startNodeIndex !== undefined && endNodeIndex !== undefined) break;
		}
		if (startNodeIndex === undefined || endNodeIndex === undefined) return [];

		// The editor gives IDs in selection direction (can be right-to-left), so we use
		// min/max to get everything between the two anchors regardless of order.
		const from = Math.min(startNodeIndex, endNodeIndex);
		const to = Math.max(startNodeIndex, endNodeIndex);

		return [
			...new Set(
				topLevelNodes
					.slice(from, to + 1)
					.map((n) => n.type)
					.filter(Boolean),
			),
		];
	} catch {
		// swallow the error and return nothing since this is just for analytics.
		return [];
	}
};

/**
 * Counts words across all text nodes in an ADF fragment.
 */
export const countFragmentWords = (fragmentNodes: ADFEntity[]): number =>
	fragmentNodes.reduce(
		(total, node) =>
			reduce<number>(
				node,
				(count, n: ADFEntity) => (n.type === 'text' ? count + wordCount(n.text) : count),
				total,
			),
		0,
	);

/**
 * Returns true if the sent message is related to the prompt card (so we should fire analytics).
 * Thin wrapper around getPromptCardSendType for backward compatibility.
 */
export const isSentTextRelatedToPromptCard = (
	normalizedSentText: string,
	normalizedOriginalPrompt: string,
): boolean => getPromptCardSendType(normalizedSentText, normalizedOriginalPrompt) !== null;
